/**
 * İşçilik alacakları hesabını WORD belgesine döker.
 *
 * NEDEN WORD?
 * -----------
 * jsPDF'in gömülü fontları ğ, ş, İ, ı karakterlerini desteklemiyor;
 * PDF'te bu harfler sadeleşiyordu. Word belgeleri UTF-8 tabanlı
 * olduğu için Türkçe KUSURSUZ çıkar ve font gömmeye gerek kalmaz.
 *
 * Yöntem: Word, HTML tabanlı .doc dosyalarını yerel biçimi gibi açar.
 * Böylece ek bir kütüphane (docx/JSZip) gerekmeden, tablo biçimlendirmesi
 * korunarak belge üretilebiliyor.
 */

import { formatTL } from './iscilikAlacaklari';

const esc = (s) =>
  String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

const tarihTR = (d) => {
  if (!d) return '-';
  const s = String(d).slice(0, 10);
  const [y, m, g] = s.split('-');
  return y ? `${g}.${m}.${y}` : s;
};

/**
 * @param {object} sonuc hesaplaTumAlacaklar çıktısı
 * @param {object} bilgi { foyNo, muvekkil, isveren, giris, cikis, ciplak, giydirilmis }
 * @returns {{blob: Blob, dosyaAdi: string}}
 */
export function iscilikWordUret(sonuc, bilgi = {}) {
  const bilgiSatirlari = [
    ['Föy No', bilgi.foyNo ? String(bilgi.foyNo) : '-'],
    ['Müvekkil', bilgi.muvekkil || '-'],
    ['İşveren', bilgi.isveren || '-'],
    ['İşe giriş', tarihTR(bilgi.giris)],
    ['İşten çıkış', tarihTR(bilgi.cikis)],
    ['Çalışma süresi', `${sonuc?.sure?.yil ?? 0} yıl ${sonuc?.sure?.kalanGun ?? 0} gün`],
    ['Çıplak brüt ücret', `${formatTL(bilgi.ciplak)} TL`],
    ['Giydirilmiş brüt ücret', `${formatTL(bilgi.giydirilmis)} TL`],
    ['Kıdem tazminatı tavanı', `${formatTL(sonuc?.tavan)} TL`],
  ];

  const bilgiHtml = bilgiSatirlari
    .map(([k, v]) => `<tr><td class="k">${esc(k)}</td><td class="v">${esc(v)}</td></tr>`)
    .join('');

  const kalemHtml = (sonuc?.kalemler || [])
    .map((k) => `
      <tr>
        <td>${esc(k.tur)}${k.aciklama ? `<div class="alt">${esc(k.aciklama)}</div>` : ''}</td>
        <td class="s">${formatTL(k.brut)}</td>
        <td class="s">${formatTL(k.gelirVergisi || 0)}</td>
        <td class="s">${formatTL(k.damga || 0)}</td>
        <td class="s b">${formatTL(k.net)}</td>
      </tr>`)
    .join('');

  const html = `<!doctype html>
<html xmlns:o="urn:schemas-microsoft-com:office:office"
      xmlns:w="urn:schemas-microsoft-com:office:word"
      xmlns="http://www.w3.org/TR/REC-html40">
<head>
<meta charset="utf-8">
<title>İşçilik Alacakları Hesap Tablosu</title>
<!--[if gte mso 9]><xml>
<w:WordDocument><w:View>Print</w:View><w:Zoom>100</w:Zoom></w:WordDocument>
</xml><![endif]-->
<style>
  @page { size: A4 portrait; margin: 2cm 1.8cm; }
  body { font-family: "Calibri", "Segoe UI", sans-serif; font-size: 10.5pt; color: #1a1a1a; }
  h1 { font-size: 15pt; margin: 0 0 4pt; color: #2b3652; }
  .ust { font-size: 8.5pt; color: #666; margin: 0 0 14pt; }
  table { border-collapse: collapse; width: 100%; margin-bottom: 14pt; }
  .bilgi td { padding: 3pt 0; font-size: 10pt; border: none; }
  .bilgi .k { width: 38%; color: #555; }
  .bilgi .v { font-weight: bold; }
  .kalem th {
    background: #2b3652; color: #fff; font-size: 9pt;
    padding: 6pt 6pt; text-align: left; border: 1px solid #2b3652;
  }
  .kalem td { padding: 6pt; border: 1px solid #d9dee6; font-size: 10pt; vertical-align: top; }
  .kalem .s { text-align: right; }
  .kalem .b { font-weight: bold; }
  .kalem .alt { font-size: 8.5pt; color: #777; margin-top: 2pt; }
  .kalem tfoot td { background: #eef2f7; font-weight: bold; }
  .not { font-size: 8pt; color: #666; line-height: 1.5; margin-top: 10pt; }
  .not li { margin-bottom: 2pt; }
</style>
</head>
<body>
  <h1>İŞÇİLİK ALACAKLARI HESAP TABLOSU</h1>
  <p class="ust">Düzenleme tarihi: ${new Date().toLocaleDateString('tr-TR')}</p>

  <table class="bilgi">${bilgiHtml}</table>

  <table class="kalem">
    <thead>
      <tr>
        <th>Alacak Kalemi</th>
        <th class="s">Brüt</th>
        <th class="s">Gelir V.</th>
        <th class="s">Damga V.</th>
        <th class="s">Net</th>
      </tr>
    </thead>
    <tbody>${kalemHtml}</tbody>
    <tfoot>
      <tr>
        <td>TOPLAM</td>
        <td class="s">${formatTL(sonuc?.toplam?.brut)}</td>
        <td class="s">${formatTL(sonuc?.toplam?.gelirVergisi)}</td>
        <td class="s">${formatTL(sonuc?.toplam?.damga)}</td>
        <td class="s b">${formatTL(sonuc?.toplam?.net)}</td>
      </tr>
    </tfoot>
  </table>

  <div class="not">
    <b>Hesap esasları</b>
    <ul>
      <li>2026 gelir vergisi tarifesi (31.12.2025 tarihli Resmî Gazete), SGK işçi payı %14 + işsizlik %1, damga vergisi binde 7,59.</li>
      <li>Kıdem tazminatından yalnızca damga vergisi kesilir; gelir vergisi ve SGK primi alınmaz.</li>
      <li>Giydirilmiş ücret kıdem tavanını aşarsa hesap tavandan yapılır (1475 s.K. m.14).</li>
      <li>Çalışma süresi, işe giriş ve çıkış tarihleri arasındaki takvim süresi üzerinden 365 gün esasıyla hesaplanmıştır.</li>
      <li>Bu tablo bilgilendirme amaçlıdır; resmî bilirkişi raporu yerine geçmez.</li>
    </ul>
  </div>
</body>
</html>`;

  // BOM: Word'ün UTF-8'i doğru algılaması için
  const blob = new Blob(['﻿', html], { type: 'application/msword' });
  const ad = `iscilik-alacaklari-${bilgi.foyNo || 'hesap'}-${new Date().toISOString().slice(0, 10)}.doc`;
  return { blob, dosyaAdi: ad };
}
